/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from 'react';
import { FlexContainer, Label, useDeviceSize } from '@facephi/ui-react';
import { useTranslation } from 'react-i18next';
import { generatePath, useParams } from 'react-router-dom';
import { useOperation, useData } from '../../providers';
import {
  RoutesName,
  FacialAuthenticationStatus,
  circleIconData,
  IconInfo,
  fullNameKey,
  MimeType,
  VerificationResults,
  DOCUMENT_FACIAL,
  Asset,
  GovernmentServices,
  datesKeys,
  governmentValitionKeys,
  GovernementServices,
} from '../../states';
import { objectMapping, transformDate, addBase64Header } from '../../utils';
import { CardResults, CardResultsContent } from '../cards';
import { CardList, DataList } from '../cards/CardList';
import { GoBackHeader } from '../goBackHeader';
import { Banner } from './Banner';
import { SelfieValidations } from './SelfieValidation';
import { ResultsWrapper } from './Styles';

export const Government = () => {
  const { isMobile } = useDeviceSize();
  const { t } = useTranslation();
  const { operationId } = useParams();
  const { opreationAssets } = useOperation();
  const { civilRegistryState } = useData();

  const { loading, error, data: governmentData } = civilRegistryState;

  const transformGovernmentData = (personalGovernmentData: any) => {
    const isReversed =
      personalGovernmentData.source === GovernmentServices.reniec;
    return transformDate(
      datesKeys,
      objectMapping(personalGovernmentData.data, governmentValitionKeys),
      isReversed
    );
  };

  const governmentObtainedData = transformGovernmentData(governmentData);
  const { facialMatchingStatus } = governmentData?.summary || {};

  const hasData = !!Object.keys(governmentData?.data || {}).length;
  const verifResult: DataList[] = useMemo(() => {
    return [
      {
        title: VerificationResults.verificationResult,
        value: error
          ? VerificationResults.VALIDATION_CONNECTION_ERROR
          : hasData
          ? VerificationResults.VALIDATION_PROVEN_CONTENT
          : VerificationResults.VALIDATION_NOT_PROVEN_CONTENT,
        valueStatus: error ? 'warning400' : hasData ? 'success400' : 'error400',
      },
    ];
  }, [governmentData]);

  const governmentValidationData = Object.entries(
    governmentObtainedData || {}
  ).reduce((acc, [currentKey, currentValue]) => {
    acc.push({
      title: currentKey,
      value: currentValue as string,
      valueStatus:
        currentKey === fullNameKey && hasData
          ? 'success400'
          : currentKey === fullNameKey && !hasData
          ? 'error400'
          : undefined,
    });
    return acc;
  }, [] as DataList[]);

  const governmentFacialCapture: Asset | undefined = useMemo(() => {
    if (governmentData?.details?.picture) {
      return {
        contentType: MimeType.jpeg,
        type: DOCUMENT_FACIAL,
        url: addBase64Header(governmentData.details?.picture, MimeType.jpeg),
        source:
          GovernementServices[
            governmentData.data.serviceDocument.BACKSIDE.MRZ_DATA
              .ISSUING_COUNTRY as keyof typeof GovernementServices
          ],
      };
    } else {
      return undefined;
    }
  }, [governmentData]);

  const governmentProvider =
    GovernementServices[
      governmentData?.data.serviceDocument.BACKSIDE.MRZ_DATA
        .ISSUING_COUNTRY as keyof typeof GovernementServices
    ];

  const cardListIcon: IconInfo | undefined = useMemo(
    () =>
      governmentValidationData.length && !error
        ? circleIconData['success']
        : !governmentValidationData.length
        ? circleIconData['error']
        : loading
        ? undefined
        : circleIconData['warning'],
    [governmentValidationData]
  );

  governmentValidationData.unshift(...verifResult);

  return (
    <>
      <GoBackHeader
        route={generatePath(RoutesName.results, {
          operationId,
        })}
        linkLabel="Go back"
        title="Government"
        linkId="governmental_return_button"
        idTitle="governmental_title"
      />
      <ResultsWrapper flexDirection="column" rowGap="1.6">
        <Banner margin={false} idDocument="governmental_subtitle" />

        {(loading || facialMatchingStatus) && (
          <CardResults
            title={t('GOVERNMENT SERVICE') || ''}
            idTitle="governmental_matching_title"
          >
            <CardResultsContent
              flexDirection="column"
              alignItems={isMobile ? 'start' : 'center'}
              rowGap="1.6"
            >
              <Label
                fontSize="21"
                semibold
                upperCase
                id="governmental_matching_subtitle"
                testId="governmental_matching_subtitle"
              >
                {t('FACE VERIFICATION')}
              </Label>

              <SelfieValidations
                loading={loading}
                assets={opreationAssets}
                facialState={facialMatchingStatus as FacialAuthenticationStatus}
                governmentFacialCapture={governmentFacialCapture}
                provider={governmentProvider}
                idMatchingSelphi="governmental_matching_photo_selfie"
                idMatchingPhotoDocument="governmental_matching_photo_governmental"
                idResultMatchingIcon="governmental_matching_icon"
                idMatchingResult="governmental_matching_result_message"
                idLabelSelphi="governmental_matching_photo_selfie_label"
                idLabelPhotoDocument="governmental_matching_photo_governmental_label"
                isDetail
              />
            </CardResultsContent>
          </CardResults>
        )}
        <FlexContainer
          columnGap={isMobile ? '0' : '0.8'}
          rowGap={isMobile ? '0.8' : '0'}
          as="section"
          flexDirection={isMobile ? 'column' : 'row'}
        >
          <CardList
            title={t('Obtained data')}
            subTitle={t('DATA VERIFICATION')}
            icon={cardListIcon}
            data={error ? verifResult : governmentValidationData}
            loading={loading}
            error={error}
            columnsPerRow={isMobile || loading || error ? '1' : '2'}
            testId="gvmnt-personal-data"
            idTitle="governmental_obtained_data_title"
            idSubtitle="governmental_obtained_data_subtitle"
            cardId="governmental_obtained_data"
            ignoreSubtitleColor
          />
        </FlexContainer>
      </ResultsWrapper>
    </>
  );
};
